import { Injectable } from '@angular/core';
import { transaction } from '@datorama/akita';
import {
  RealtimeBatchActivityMessage,
  RealtimeBatchActivitySyncPayload,
} from '@common/api/realtime';
import { IBaseEntity } from '@common/interfaces/base';
import { Topics } from '@common/interfaces/topics';
import { ActivityService } from '@ep-om/project/service/activity.service';
import { RealtimeV2EntitySyncService } from './realtime-v2-entity-sync.service';

@Injectable({
  providedIn: 'root',
})
export class RealtimeV2BatchActivitySyncService {
  constructor(
    private activityService: ActivityService,
    private entitySync: RealtimeV2EntitySyncService,
  ) {}

  // Un chunk applica N activity in sequenza e ogni removal cascata su piu' store (issue,
  // commenti, allegati, interfacce, ...): la transazione Akita rimanda le notifiche alla fine,
  // cosi' ogni store toccato emette una volta sola per chunk invece che una per scrittura.
  // Le transazioni annidate (applyEntityBatch e' gia' @transaction) si compongono via contatore.
  @transaction()
  applyBatchActivitySync(payload: RealtimeBatchActivitySyncPayload): void {
    const activities = payload.data.activities || [];

    for (const activity of activities) {
      this.applyActivity(payload.data.scopeKey, activity);
    }

    this.updateCursor(payload);
  }

  private applyActivity(scopeKey: string, activity: RealtimeBatchActivityMessage): void {
    switch (activity.action) {
      case 'checkpoint':
        return;
      case 'remove_issue':
        this.removeIssue(activity);
        return;
      case 'remove_project':
        this.removeProject(activity);
        return;
      case 'remove_appointment':
        this.removeAppointment(activity);
        return;
      case 'remove_appointments_by_user':
        this.removeAppointmentsByUser(activity);
        return;
      case 'appointment_resync':
        // Resync vero: svuotiamo gli appuntamenti locali e azzeriamo il cursore; l'ack parte dal
        // dispatcher. Poi il BE ri-streamma gli appuntamenti in pagine (`appointment_resync_batch`),
        // gestite sotto, ripopolando store e cursore da zero.
        this.activityService.clearAppointmentsForResync();
        this.entitySync.resetAppointmentCursor();
        return;
      case 'issue_visible':
      case 'appointment_visible':
        this.applyVisibleEntities(scopeKey, activity);
        return;
      case 'appointment_resync_batch':
        // Pagine di resync: applichiamo gli appuntamenti come per le altre entita' "visible".
        // L'avanzamento del cursore Appointment e' gestito in updateCursor (gating sull'action).
        this.applyVisibleEntities(scopeKey, activity);
        return;
    }
  }

  private removeIssue(activity: RealtimeBatchActivityMessage): void {
    const issueId = (activity.payload as { issueId?: string }).issueId;
    if (!issueId) {
      return;
    }

    this.activityService.removeIssue(issueId);
  }

  private removeProject(activity: RealtimeBatchActivityMessage): void {
    const projectId = (activity.payload as { projectId?: string }).projectId;
    if (!projectId) {
      return;
    }

    this.activityService.removeProject(projectId);
  }

  private removeAppointment(activity: RealtimeBatchActivityMessage): void {
    const appointmentId = (activity.payload as { appointmentId?: string }).appointmentId;
    if (!appointmentId) {
      return;
    }

    this.activityService.removeAppointmentNotInteresting(JSON.stringify({ appointmentId }));
    // La rimozione locale puo' aver portato via l'entita' ancorata dal cursore Appointment:
    // senza ri-ancoraggio, al prossimo refresh reconcilePersistedCursors lo dropperebbe
    // forzando un resync completo.
    this.entitySync.reanchorCursorAfterLocalRemoval(Topics.APPOINTMENT);
  }

  private removeAppointmentsByUser(activity: RealtimeBatchActivityMessage): void {
    const userIds = (activity.payload as { userIds?: string[] }).userIds || [];
    if (!userIds.length) {
      return;
    }

    this.activityService.removeAppointmentsByUser(userIds);
    this.entitySync.reanchorCursorAfterLocalRemoval(Topics.APPOINTMENT);
  }

  private applyVisibleEntities(scopeKey: string, activity: RealtimeBatchActivityMessage): void {
    const entities = (activity.payload as { entities?: { entityName: string; entities: IBaseEntity[] }[] }).entities || [];

    for (const response of entities) {
      this.entitySync.applyEntityResponse(scopeKey, response);
    }
  }

  private updateCursor(payload: RealtimeBatchActivitySyncPayload): void {
    const lastActivity = payload.data.activities?.at(-1);
    if (!lastActivity) {
      return;
    }

    // Regola generale: una batch activity avanza SOLO il proprio cursore (lo stream batch),
    // mai il cursore dell'entita' a cui si riferisce. Unica eccezione: le pagine di
    // `appointment_resync_batch`, che fanno avanzare il cursore entita' Appointment (id/updatedAt
    // dell'ultimo appuntamento della pagina), cosi' il resync e' incrementale come un first-sync.
    // Le pagine di resync contengono solo appuntamenti vivi (il BE esclude cancellati/archiviati),
    // quindi l'ultimo appuntamento vale anche come ancora in-store del cursore.
    const isResyncBatch = lastActivity.action === 'appointment_resync_batch';

    this.entitySync.updateCursorFromEntity(
      payload.data.scopeKey,
      isResyncBatch ? Topics.APPOINTMENT : payload.data.entityName,
      lastActivity,
      payload.remaining,
      isResyncBatch ? lastActivity : undefined,
    );
  }
}
